"use client";

import { MagnifyingGlass, X, LockSimple } from "@phosphor-icons/react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
  type ReactElement,
} from "react";
import { cn } from "../lib/utils";
import { useKitseed } from "../lib/provider";
import "./patterns.css";

export type SearchFieldProps = Omit<
  ComponentProps<"input">,
  "value" | "onChange" | "onSearch" | "type" | "size"
> & {
  value: string;
  onValueChange: (value: string) => void;
  onSearch?: (value: string) => void;
  label: string;
  clearLabel?: string;
  debounceMs?: number;
  containerClassName?: string;
  unstyled?: boolean;
  onClear?: () => void;
  slotProps?: {
    icon?: ComponentProps<typeof MagnifyingGlass>;
    clearButton?: ComponentProps<"button">;
    clearIcon?: ComponentProps<typeof X>;
  };
};

/** Changes are immediate. Search commits are trimmed, debounced, IME aware, and deduplicated. */
export function SearchField({
  value,
  onValueChange,
  onSearch,
  label,
  clearLabel = "清除搜索",
  debounceMs = 300,
  containerClassName,
  className,
  disabled,
  onKeyDown,
  onCompositionStart,
  onCompositionEnd,
  unstyled = false,
  onClear,
  slotProps = {},
  ...props
}: SearchFieldProps) {
  const { density } = useKitseed();
  const [composing, setComposing] = useState(false);
  const composition = useRef(false);
  const lastSearch = useRef(value.trim());
  const callbacks = useRef({ onSearch, onValueChange });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  callbacks.current = { onSearch, onValueChange };
  const clearTimer = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const commit = useCallback(
    (text: string) => {
      clearTimer();
      const next = text.trim();
      if (next === lastSearch.current) return;
      lastSearch.current = next;
      callbacks.current.onSearch?.(next);
    },
    [clearTimer],
  );
  useEffect(() => {
    clearTimer();
    if (!composing && !disabled && value.trim() !== lastSearch.current) {
      timer.current = setTimeout(() => commit(value), Math.max(0, debounceMs));
    }
    return clearTimer;
  }, [value, composing, disabled, debounceMs, commit, clearTimer]);
  return (
    <div
      role="search"
      data-density={unstyled ? undefined : density}
      className={cn(
        !unstyled &&
          "ks-search-field flex min-w-0 items-center gap-2 rounded-control border border-line bg-panel px-2.5 text-fg-muted transition-colors focus-within:border-brand-line focus-within:ring-2 focus-within:ring-brand-line/15",
        !unstyled && disabled && "opacity-50",
        containerClassName,
      )}
    >
      <MagnifyingGlass
        size={15}
        aria-hidden="true"
        {...slotProps.icon}
        className={cn(!unstyled && "shrink-0 text-fg-subtle", slotProps.icon?.className)}
      />
      <input
        {...props}
        disabled={disabled}
        type="search"
        value={value}
        aria-label={label}
        onChange={(event) => callbacks.current.onValueChange(event.target.value)}
        onCompositionStart={(event) => {
          composition.current = true;
          setComposing(true);
          clearTimer();
          onCompositionStart?.(event);
        }}
        onCompositionEnd={(event) => {
          composition.current = false;
          callbacks.current.onValueChange(event.currentTarget.value);
          setComposing(false);
          onCompositionEnd?.(event);
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (
            event.defaultPrevented ||
            composition.current ||
            event.nativeEvent.isComposing ||
            event.keyCode === 229
          )
            return;
          if (event.key === "Enter") {
            event.preventDefault();
            commit(event.currentTarget.value);
          }
          if (event.key === "Escape" && value) {
            event.preventDefault();
            callbacks.current.onValueChange("");
            commit("");
          }
        }}
        className={cn(
          !unstyled &&
            "w-full min-w-0 flex-1 bg-transparent py-2 text-[13px] text-fg outline-none placeholder:text-fg-subtle [&::-webkit-search-cancel-button]:appearance-none",
          className,
        )}
      />
      {value ? (
        <button
          {...slotProps.clearButton}
          type="button"
          disabled={disabled}
          aria-label={clearLabel}
          onClick={() => {
            callbacks.current.onValueChange("");
            commit("");
            onClear?.();
          }}
          className={cn(
            !unstyled &&
              "ks-search-clear grid shrink-0 place-items-center rounded-control text-fg-subtle outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-brand-line",
            slotProps.clearButton?.className,
          )}
        >
          <X size={13} aria-hidden="true" {...slotProps.clearIcon} />
        </button>
      ) : null}
    </div>
  );
}

export type SwitchFieldProps = Omit<
  ComponentProps<"input">,
  "type" | "checked" | "onChange" | "size" | "children"
> & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  containerClassName?: string;
};

export function SwitchField({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
  containerClassName,
  className,
  id: suppliedId,
  "aria-describedby": describedBy,
  ...props
}: SwitchFieldProps) {
  const generatedId = useId();
  const id = suppliedId ?? generatedId;
  return (
    <label
      htmlFor={id}
      className={cn(
        "ks-switch-field flex min-w-0 cursor-pointer items-center justify-between gap-5 py-2",
        disabled && "cursor-default opacity-50",
        containerClassName,
      )}
    >
      <span className="min-w-0">
        <span className="block text-[13px] font-medium text-fg">{label}</span>
        {description ? (
          <span id={`${id}-description`} className="mt-1 block text-xs leading-5 text-fg-muted">
            {description}
          </span>
        ) : null}
      </span>
      <span className="relative inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center">
        <input
          {...props}
          id={id}
          role="switch"
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-describedby={
            [description ? `${id}-description` : "", describedBy].filter(Boolean).join(" ") ||
            undefined
          }
          onChange={(event) => onCheckedChange(event.target.checked)}
          className={cn(
            "peer absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-default",
            className,
          )}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none h-5 w-8 rounded-full bg-line-strong transition-colors peer-checked:bg-brand peer-focus-visible:ring-2 peer-focus-visible:ring-brand-line peer-focus-visible:ring-offset-2 motion-reduce:transition-none"
        >
          <span
            className={cn(
              "mt-0.5 block size-4 rounded-full bg-white shadow-xs transition-transform motion-reduce:transition-none",
              checked ? "translate-x-3.5" : "translate-x-0.5",
            )}
          />
        </span>
      </span>
    </label>
  );
}

type DataAttributes = { [key: `data-${string}`]: string | number | boolean | undefined };
type SpanSlot = ComponentProps<"span"> & DataAttributes;

export type FilterChipProps = {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  valueIcon?: ReactNode;
  operator?: ReactNode;
  onEdit?: () => void;
  onRemove?: () => void;
  editLabel?: string;
  removeLabel?: string;
  removeDisabledReason?: string;
  expanded?: boolean;
  popup?: "menu" | "dialog";
  readOnly?: boolean;
  className?: string;
  unstyled?: boolean;
  removableWhenReadOnly?: boolean;
  editable?: boolean;
  showOperator?: boolean;
  renderEdit?: (element: ReactElement) => ReactNode;
  renderRemove?: (element: ReactElement) => ReactNode;
  slotProps?: {
    root?: SpanSlot;
    label?: SpanSlot;
    operator?: SpanSlot;
    value?: SpanSlot;
    valueText?: SpanSlot;
    staticContent?: SpanSlot;
    edit?: ComponentProps<"button">;
    remove?: ComponentProps<"button">;
    removeIcon?: ComponentProps<typeof X>;
    lockedIcon?: ComponentProps<typeof LockSimple>;
    disabledDescription?: SpanSlot;
  };
};

export function FilterChip({
  label,
  value,
  icon,
  valueIcon,
  operator,
  onEdit,
  onRemove,
  editLabel = `编辑${label}筛选`,
  removeLabel = `移除${label}筛选`,
  removeDisabledReason,
  expanded,
  popup,
  readOnly = false,
  className,
  unstyled = false,
  removableWhenReadOnly = false,
  editable = Boolean(onEdit),
  showOperator = Boolean(operator),
  renderEdit = (element) => element,
  renderRemove = (element) => element,
  slotProps = {},
}: FilterChipProps) {
  const { density } = useKitseed();
  const id = useId();
  const content = (
    <>
      <span
        {...slotProps.label}
        className={cn(
          !unstyled && "flex items-center gap-1.5 px-2 text-fg-muted",
          slotProps.label?.className,
        )}
      >
        {icon}
        {label}
      </span>
      {showOperator ? (
        <span
          aria-hidden="true"
          {...slotProps.operator}
          className={cn(
            !unstyled && "border-l border-line px-2 text-fg-subtle",
            slotProps.operator?.className,
          )}
        >
          {operator}
        </span>
      ) : null}
      <span
        {...slotProps.value}
        className={cn(
          !unstyled &&
            "flex min-w-0 items-center gap-1.5 border-l border-line px-2 font-medium text-fg",
          slotProps.value?.className,
        )}
      >
        {valueIcon}
        <span
          {...slotProps.valueText}
          className={cn(!unstyled && "max-w-40 truncate", slotProps.valueText?.className)}
        >
          {value}
        </span>
      </span>
    </>
  );
  return (
    <span
      {...slotProps.root}
      data-density={unstyled ? undefined : density}
      className={cn(
        !unstyled &&
          "ks-filter-chip inline-flex max-w-full items-stretch overflow-hidden rounded-control border border-line-strong bg-panel text-[13px] shadow-xs",
        slotProps.root?.className,
        className,
      )}
    >
      {editable && !readOnly ? (
        renderEdit(
          <button
            {...slotProps.edit}
            type="button"
            aria-label={editLabel}
            aria-haspopup={popup}
            aria-expanded={expanded}
            onClick={onEdit}
            className={cn(
              !unstyled &&
                "flex min-w-0 items-center outline-none hover:bg-hover focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-line",
              slotProps.edit?.className,
            )}
          >
            {content}
          </button>,
        )
      ) : (
        <span
          {...slotProps.staticContent}
          className={cn(
            !unstyled && "flex min-w-0 items-center",
            slotProps.staticContent?.className,
          )}
        >
          {content}
        </span>
      )}
      {onRemove && (!readOnly || removableWhenReadOnly)
        ? renderRemove(
            <button
              aria-label={removeLabel}
              aria-disabled={Boolean(removeDisabledReason) || undefined}
              aria-describedby={removeDisabledReason ? id : undefined}
              title={removeDisabledReason}
              {...slotProps.remove}
              type="button"
              onClick={removeDisabledReason ? undefined : onRemove}
              className={cn(
                !unstyled &&
                  "ks-chip-remove grid shrink-0 place-items-center border-l border-line text-fg-subtle outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-line",
                !unstyled &&
                  (removeDisabledReason
                    ? "cursor-not-allowed opacity-50"
                    : "hover:bg-hover hover:text-fg"),
                slotProps.remove?.className,
              )}
            >
              {removeDisabledReason ? (
                <LockSimple size={12} aria-hidden="true" {...slotProps.lockedIcon} />
              ) : (
                <X size={12} aria-hidden="true" {...slotProps.removeIcon} />
              )}
            </button>,
          )
        : null}
      {removeDisabledReason ? (
        <span
          id={id}
          {...slotProps.disabledDescription}
          className={cn(!unstyled && "sr-only", slotProps.disabledDescription?.className)}
        >
          {removeDisabledReason}
        </span>
      ) : null}
    </span>
  );
}

export function ChoiceContent({
  icon,
  showIcon = Boolean(icon),
  title,
  description,
  status,
  unstyled = false,
  slotProps = {},
  renderTitle = (element) => element,
}: {
  icon?: ReactNode;
  showIcon?: boolean;
  title: ReactNode;
  description?: ReactNode;
  status?: ReactNode;
  unstyled?: boolean;
  renderTitle?: (element: ReactElement) => ReactNode;
  slotProps?: {
    root?: SpanSlot;
    icon?: SpanSlot;
    content?: SpanSlot;
    title?: SpanSlot;
    description?: SpanSlot;
    status?: SpanSlot;
  };
}) {
  return (
    <span
      {...slotProps.root}
      className={cn(!unstyled && "flex min-w-0 items-start gap-2.5", slotProps.root?.className)}
    >
      {showIcon ? (
        <span
          aria-hidden="true"
          {...slotProps.icon}
          className={cn(!unstyled && "mt-0.5 shrink-0", slotProps.icon?.className)}
        >
          {icon}
        </span>
      ) : null}
      <span
        {...slotProps.content}
        className={cn(!unstyled && "min-w-0 flex-1", slotProps.content?.className)}
      >
        {renderTitle(
          <span
            {...slotProps.title}
            className={cn(
              !unstyled && "block text-[13px] font-medium text-fg [overflow-wrap:anywhere]",
              slotProps.title?.className,
            )}
          >
            {title}
          </span>,
        )}
        {description ? (
          <span
            {...slotProps.description}
            className={cn(
              !unstyled && "mt-0.5 block text-xs leading-5 text-fg-subtle",
              slotProps.description?.className,
            )}
          >
            {description}
          </span>
        ) : null}
        {status ? (
          <span
            {...slotProps.status}
            className={cn(
              !unstyled && "mt-0.5 block text-xs leading-5 text-fg-muted",
              slotProps.status?.className,
            )}
          >
            {status}
          </span>
        ) : null}
      </span>
    </span>
  );
}
